import React, { CSSProperties, PropsWithChildren, ReactNode, Ref } from 'react';
import { defined } from '@unifocus/js-utils';
import { Property } from 'csstype';
import { PaddingType } from '../../panel';
import { FlexPanel } from '../../flexpanel';
import { MappedFlexPanels } from './MappedFlexPanels';

export type TLength = string | number;

export type BreakpointWidth = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

export type FlexItemDefinition = {
   order?: number;
   flex?: number | string;
   alignSelf?: Property.AlignSelf,
   justifySelf?: Property.JustifySelf,
   width?: Property.Width<TLength>;
   height?: Property.Height<TLength>;
   minWidth?: Property.MinWidth<TLength>;
   maxWidth?: Property.MaxWidth<TLength>;
   minHeight?: Property.MinHeight<TLength>;
   maxHeight?: Property.MaxHeight<TLength>;
} & FlexPanelDefinition;

export type FlexItemMap = {
   [areaName: string]: FlexItemDefinition;
};

export type FlexPanelDefinition = {
   flexDirection?: Property.FlexDirection;
   flexWrap?: Property.FlexWrap;
   justifyContent?: Property.JustifyContent;
   alignItems?: Property.AlignItems;
   alignContent?: Property.AlignContent;
   flexItems?: FlexItemMap;
}

export type FlexTemplateDefinition = {
   autoOverflowAreas?: string[] | boolean;
   rows: FlexItemDefinition[]
}

export type GapProperty = boolean | 'small' | 'medium' | 'large';

export interface FlexTemplatePanelProps {
   /**
    * The template to use.
    */
   template: FlexTemplateDefinition;

   /**
    * The gap between the columns and rows.
    *
    * @defaultvalue medium
    */
   gap?: GapProperty;

   /**
    * The gap between the columns.
    *
    * @defaultvalue medium
    */
   hgap?: GapProperty;

   /**
    * The gap between the rows.
    *
    * @defaultvalue medium
    */
   vgap?: GapProperty;

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * The width of the panel.
    *
    * @defaultvalue 100%
    */
   width?: string | number;

   /**
    * The height of the panel.
    *
    * @defaultvalue 100%
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * The children TemplateAreas
    */
   children?: ReactNode;
}

function isFlexTemplateDefinition(obj: any): obj is FlexTemplateDefinition {
   return obj && obj.rows !== undefined;
}

export const FlexTemplatePanel = React.forwardRef<HTMLDivElement, FlexTemplatePanelProps>(
   (props: FlexTemplatePanelProps, ref: Ref<HTMLDivElement>) => {

      const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : false;
      const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : false;

      const hgap = defined(props.hgap) ? props.hgap : props.gap;
      const vgap = defined(props.vgap) ? props.vgap : props.gap;

      const template = props.template;

      let classNames = 'uf-template-panel uf-template-panel-flex';

      if (props.className) {
         classNames += ' ' + props.className;
      }

      return (<FlexPanel
         hgap={hgap}
         vgap={vgap}
         className={classNames}
         border={props.border}
         padding={props.padding}
         paddingLeft={props.paddingLeft}
         paddingRight={props.paddingRight}
         paddingTop={props.paddingTop}
         paddingBottom={props.paddingBottom}
         scroll={props.scroll}
         width={props.width || 'auto'}
         height={props.height}
         minHeight={props.minHeight}
         minWidth={props.minWidth}
         maxHeight={props.maxHeight}
         maxWidth={props.maxWidth}
         verticalFill={verticalFill}
         horizontalFill={horizontalFill}
         flexDirection={'column'}
         style={props.style}
         ref={ref}
         id={props.id}
      >
         <MappedFlexPanels flexTemplate={template} hgap={hgap} vgap={vgap}>
            {props.children}
         </MappedFlexPanels>
      </FlexPanel>);
   }
);
